import { useCallback, useEffect, useRef, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import SignaturePad from "signature_pad"
import { Api, downloadFile, urlWithSignerAccessCode } from "../../../api/Assinafy"
import { getSignedVerificationMethodForNavigate } from "../../../utils/verificationMethodFromDocument"
import { hasUsableSignature, isMissingStoredSignatureError, readApiErrorMessage } from "../../../utils/signerSignature"
import { Alert, Button, Form, Icon, Input, Tabs } from "../../../components"
import { SignNavbar } from "../../../layout/Sign/Sign"
import ModalDeny from "./ModalDeny"
import ModalSignatureCrop from "./ModalSignatureCrop"
import "../styles.scss"
import "./signature-page.scss"

const fonts = window.WebFontFamilies

const fitFontSizeToWidth = (ctx, text, fontFamily, maxWidth, startSize = 26, minSize = 14) => {
  let size = startSize
  ctx.font = `${size}px '${fontFamily}'`
  let width = ctx.measureText(text).width

  while (width > maxWidth && size > minSize) {
    size -= 1
    ctx.font = `${size}px '${fontFamily}'`
    width = ctx.measureText(text).width
  }

  return size
}

const blobFromCanvas = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error("Falha ao gerar imagem da assinatura"))
      },
      "image/png",
      1.0,
    )
  })

const loadImageOnCanvas = (canvas, url) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const ctx = canvas.getContext("2d")
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const margin = 16
      const maxWidth = canvas.width - margin * 2
      const maxHeight = canvas.height - margin * 2
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1)
      const width = img.width * scale
      const height = img.height * scale
      const x = (canvas.width - width) / 2
      const y = (canvas.height - height) / 2

      ctx.drawImage(img, x, y, width, height)
      resolve()
    }
    img.onerror = () => reject(new Error("Falha ao carregar assinatura salva"))
    img.src = url
  })

const SignaturePage = () => {
  const navigate = useNavigate()
  const { signerAccessCode } = useParams()
  const location = useLocation()
  const { state } = location

  const signDocument = state?.document
  const signer = state?.signer

  const [error, setError] = useState([])
  const [signerName, setSignerName] = useState("")
  const [signerData, setSignerData] = useState(signer || {})
  const [tabKey, setTabKey] = useState("custom")
  const [loading, setLoading] = useState(false)
  const [signaturePad, setSignaturePad] = useState(null)
  const [modalCancel, setModalCancel] = useState()
  const [reuseSignature, setReuseSignature] = useState(false)
  const [imageBlob, setImageBlob] = useState(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null)
  const [rawImageUrl, setRawImageUrl] = useState(null)
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [storedSignatureUrl, setStoredSignatureUrl] = useState(null)
  const [signatureModified, setSignatureModified] = useState(false)
  const fileInputRef = useRef(null)
  const reuseTouchedRef = useRef(false)
  const resizeTimeoutRef = useRef(null)
  const signaturePadRef = useRef(null)

  const formatSignerName = (fullName) =>
    fullName
      .toLowerCase()
      .split(/\s+/)
      .map((v) => (v && v[0] ? v[0].toUpperCase() + v.slice(1) : ""))
      .join(" ")

  useEffect(() => {
    if (!state?.verified || !state?.dataConfirmed) {
      navigate(`/verify/${signerAccessCode}`, { replace: true })
      return
    }

    Api("signer/self")(signerAccessCode)
      .then(({ status, data: { data } }) => {
        if (status !== 200) {
          return
        }

        setSignerData(data)

        if (data.full_name) {
          setSignerName(formatSignerName(data.full_name))
        }

        if (data.is_signature_reusable != null && !reuseTouchedRef.current) {
          setReuseSignature(!!data.is_signature_reusable)
        }

        if (hasUsableSignature(data)) {
          return Api("signer/signature")(signerAccessCode, "signature")
            .then(({ data: signatureBlob }) => {
              const url = URL.createObjectURL(signatureBlob)
              setStoredSignatureUrl(url)
              setImagePreviewUrl(url)
            })
            .catch(async (error) => {
              if (!(await isMissingStoredSignatureError(error))) throw error

              // A imagem cadastrada não existe mais no storage: zera as flags no
              // estado local para a página pedir uma assinatura nova em vez de
              // tentar reaproveitar um arquivo inexistente.
              setSignerData((prev) => ({ ...prev, has_signature: false, has_initial: false }))
              setStoredSignatureUrl(null)
              setImagePreviewUrl(null)
            })
        }
      })
      .catch(async (error) => {
        // Chega aqui a falha do próprio `signer/self` OU o re-throw deliberado
        // do catch acima (erro real do `signer/signature`, não "arquivo sumiu").
        // Um catch vazio anularia esse re-throw e deixaria a tela muda: sem
        // signerData, sem signerName e sem storedSignatureUrl, com nenhuma
        // mensagem para o signatário.
        const message = await readApiErrorMessage(error)
        setError([message || "Não foi possível carregar seus dados. Tente novamente."])
      })
  }, [signerAccessCode, navigate, state])

  // Retorna true se alguma largura mudou de fato (ou seja, se algum canvas foi limpo).
  const syncFluidCanvases = useCallback(() => {
    let resized = false

    document.querySelectorAll(".signature-page .signature-card canvas.fluid").forEach((canvas) => {
      const width = canvas.parentNode?.offsetWidth
      // Escrever `width` reseta o backing store e apaga o que já foi desenhado —
      // inclusive quando o valor é o mesmo. Só escreve se a largura mudou de fato,
      // senão qualquer re-render que passe por aqui apaga o traço do usuário.
      if (width && canvas.width !== width) {
        canvas.setAttribute("width", String(width))
        resized = true
      }
    })

    return resized
  }, [])

  const redrawStoredSignatureOnPad = useCallback(() => {
    if (!storedSignatureUrl || signatureModified || tabKey === "upload") {
      return
    }

    const canvas = signaturePadRef.current?.canvas || document.getElementById(`name-${tabKey}-pad`)
    if (!canvas) {
      return
    }

    loadImageOnCanvas(canvas, storedSignatureUrl).catch(() => {})
  }, [storedSignatureUrl, signatureModified, tabKey])

  const handleCanvasLayoutChange = useCallback(() => {
    const resized = syncFluidCanvases()

    // Um resize real limpa o canvas. Se o usuário já desenhou, o traço vive no
    // `_data` do signature_pad — replica ele em vez de deixar a tela em branco
    // (o redraw da assinatura salva abaixo é justamente pulado nesse caso).
    if (resized && signatureModified) {
      const pad = signaturePadRef.current
      const data = pad?.toData?.()
      if (data?.length) {
        pad.fromData(data)
      }
    }

    redrawStoredSignatureOnPad()
  }, [syncFluidCanvases, redrawStoredSignatureOnPad, signatureModified])

  useEffect(() => {
    handleCanvasLayoutChange()
  }, [tabKey, handleCanvasLayoutChange])

  useEffect(() => {
    const scheduleCanvasLayoutChange = () => {
      if (resizeTimeoutRef.current) {
        window.clearTimeout(resizeTimeoutRef.current)
      }

      resizeTimeoutRef.current = window.setTimeout(handleCanvasLayoutChange, 150)
    }

    window.addEventListener("resize", scheduleCanvasLayoutChange)

    return () => {
      window.removeEventListener("resize", scheduleCanvasLayoutChange)
      if (resizeTimeoutRef.current) {
        window.clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [handleCanvasLayoutChange])

  useEffect(() => {
    if (tabKey === "upload") {
      return undefined
    }

    const canvas = document.getElementById(`name-${tabKey}-pad`)
    const container = canvas?.parentNode
    if (!container) {
      return undefined
    }

    const resizeObserver = new ResizeObserver(() => {
      handleCanvasLayoutChange()
    })

    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [tabKey, handleCanvasLayoutChange])

  const initPadsForTab = useCallback((key) => {
    window.setTimeout(() => {
      const signaturePadElm = document.getElementById(`name-${key}-pad`)
      if (!signaturePadElm) {
        signaturePadRef.current = null
        setSignaturePad(null)
        return
      }

      const nextSignaturePad = new SignaturePad(signaturePadElm, {
        penColor: "#000F55",
        onEnd: () => setSignatureModified(true),
      })

      setSignaturePad(nextSignaturePad)
      signaturePadRef.current = nextSignaturePad
      syncFluidCanvases()
    })
  }, [syncFluidCanvases])

  useEffect(() => {
    if (!storedSignatureUrl || signatureModified || tabKey === "upload" || !signaturePad?.canvas) {
      return
    }

    handleCanvasLayoutChange()
  }, [storedSignatureUrl, signatureModified, tabKey, signaturePad, handleCanvasLayoutChange])

  useEffect(() => {
    if (!state?.verified || !state?.dataConfirmed) {
      return
    }
    if (!signerData?.full_name && !signer?.full_name) {
      return
    }
    if (tabKey === "upload") {
      signaturePadRef.current = null
      setSignaturePad(null)
      return
    }
    initPadsForTab(tabKey)
  }, [signer, signerData, state, tabKey, initPadsForTab])

  useEffect(() => {
    return () => {
      if (storedSignatureUrl) {
        URL.revokeObjectURL(storedSignatureUrl)
      } else if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl)
      }

      if (rawImageUrl) {
        URL.revokeObjectURL(rawImageUrl)
      }
    }
  }, [imagePreviewUrl, storedSignatureUrl, rawImageUrl])

  const isCanvasEmpty = (canvas) => {
    if (!canvas) return true
    const blankCanvas = document.createElement("canvas")
    blankCanvas.width = canvas.width
    blankCanvas.height = canvas.height
    return canvas.toDataURL() === blankCanvas.toDataURL()
  }

  const handleTabSelect = (nextTabKey) => {
    if (nextTabKey == null) {
      return
    }
    setTabKey(nextTabKey)
  }

  const handleImageSelect = (event) => {
    const [file] = event.target.files || []
    if (!file) {
      return
    }

    if (!file.type.startsWith("image/")) {
      setError(["Selecione um arquivo de imagem válido"])
      return
    }

    if (rawImageUrl) {
      URL.revokeObjectURL(rawImageUrl)
    }

    setRawImageUrl(URL.createObjectURL(file))
    setCropModalOpen(true)
    setError([])
  }

  const handleCropConfirm = (croppedBlob, croppedPreviewUrl) => {
    if (imagePreviewUrl && imagePreviewUrl !== storedSignatureUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }

    setImageBlob(croppedBlob)
    setImagePreviewUrl(croppedPreviewUrl)
    setSignatureModified(true)
    setCropModalOpen(false)
    setError([])
  }

  const handleCropCancel = () => {
    setCropModalOpen(false)

    // Permite reselecionar o mesmo arquivo depois de cancelar.
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }

    // Sem recorte confirmado ainda: descarta a imagem original.
    if (!imageBlob && rawImageUrl) {
      URL.revokeObjectURL(rawImageUrl)
      setRawImageUrl(null)
    }
  }

  const handleClearImage = () => {
    if (imagePreviewUrl && imagePreviewUrl !== storedSignatureUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }

    if (rawImageUrl) {
      URL.revokeObjectURL(rawImageUrl)
    }

    setImageBlob(null)
    setImagePreviewUrl(null)
    setRawImageUrl(null)
    setSignatureModified(true)

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleClearCanvas = () => {
    signaturePad?.clear()
    setSignatureModified(true)
  }

  const handleUploadAreaClick = () => {
    if (!imagePreviewUrl) {
      fileInputRef.current?.click()
    }
  }

  const handleChoosenSignature = (fontName) => {
    setSignatureModified(true)

    window.setTimeout(() => {
      if (signaturePad && signaturePad.canvas) {
        const signatureCanvas = signaturePad.canvas
        signaturePad.clear()

        const ctx = signatureCanvas.getContext("2d")
        ctx.restore()
        ctx.fillStyle = "#000F55"
        ctx.textBaseline = "middle"

        const marginX = 16
        const usableWidth = Math.max(0, signatureCanvas.width - marginX * 2)

        const fittedSize = fitFontSizeToWidth(ctx, signerName || "", fontName, usableWidth, 26, 14)

        ctx.font = `${fittedSize}px '${fontName}'`

        const x = marginX
        const y = signatureCanvas.height / 2
        ctx.fillText(signerName || "", x, y)
      }

    })
  }

  const navbarItems = () => {
    const itemsMenu = []

    itemsMenu.push({
      children: (
        <>
          <Icon id="icon-arrow_downward" className="me-1" size="20" />
          Baixar documento
        </>
      ),
      onClick: () => downloadFile(urlWithSignerAccessCode(signDocument.artifacts?.original, signerAccessCode)),
    })

    itemsMenu.push({
      children: (
        <span className="text-danger">
          <Icon id="icon-close" className="me-1" size="20" />
          Negar assinatura
        </span>
      ),
      onClick: () => {
        setModalCancel({
          description:
            "Ao recusar este documento todos os participantes serão informados e ninguém mais poderá assinar.",
        })
      },
    })

    return itemsMenu
  }

  const reusePreferenceChanged = () =>
    signerData?.is_signature_reusable != null && signerData.is_signature_reusable !== reuseSignature

  const shouldUploadNewSignature = () => {
    if (signatureModified) {
      return true
    }

    if (tabKey === "upload") {
      return Boolean(imageBlob)
    }

    if (!signaturePad?.canvas) {
      return false
    }

    return !isCanvasEmpty(signaturePad.canvas) && !storedSignatureUrl
  }

  const isUsingExistingSignature = () => hasUsableSignature(signerData) && !shouldUploadNewSignature()

  const getSignatureBlob = async () => {
    if (tabKey === "upload" && imageBlob) {
      // Já é um PNG no tamanho fixo, gerado pelo editor de crop/rotação.
      return imageBlob
    }

    if (signaturePad?.canvas && !isCanvasEmpty(signaturePad.canvas)) {
      return blobFromCanvas(signaturePad.canvas)
    }

    if (hasUsableSignature(signerData)) {
      const { data } = await Api("signer/signature")(signerAccessCode, "signature")
      return data
    }

    throw new Error("Assinatura não encontrada")
  }

  const updateReusePreference = async () => {
    const { data } = await Api("signer/signature")(signerAccessCode, "signature")
    await Api("signer/uploadSignature")(data, signerAccessCode, "signature", reuseSignature)
  }

  /**
   * A imagem salva sumiu do storage no meio do submit: descarta o estado de
   * "assinatura existente" e devolve o signer para o pad em branco, com um
   * recado acionável em vez do erro genérico.
   */
  const handleMissingStoredSignature = () => {
    setSignerData((prev) => ({ ...prev, has_signature: false, has_initial: false }))
    setStoredSignatureUrl(null)
    setImagePreviewUrl(null)
    setImageBlob(null)
    setSignatureModified(false)
    setError(["Sua assinatura salva não está mais disponível. Desenhe ou envie uma nova assinatura."])

    if (signaturePad?.canvas) {
      signaturePad.clear()
    }
  }

  const completeDocumentSign = async () => {
    if (signDocument && signDocument.assignment) {
      await Api("signer/signItems")(signDocument.id, signDocument.assignment.id, [], signerAccessCode)

      navigate(`/sign/${signerAccessCode}/success`, {
        state: {
          documentName: signDocument.name,
          verification_method: getSignedVerificationMethodForNavigate(signDocument, signerData, state),
        },
        replace: true,
      })
    }
  }

  const handleUpload = async () => {
    setError([])

    const usingExisting = isUsingExistingSignature()

    if (!usingExisting) {
      if (tabKey === "upload") {
        if (!imageBlob && !imagePreviewUrl) {
          setError(["Selecione uma imagem da assinatura"])
          return
        }
      } else if (!signaturePad) {
        setError(["Aguarde o carregamento dos campos de assinatura."])
        return
      } else if (isCanvasEmpty(signaturePad.canvas)) {
        setError(["A assinatura não pode estar em branco"])
        return
      }
    }

    setLoading(true)

    try {
      if (shouldUploadNewSignature()) {
        const signatureBlob = await getSignatureBlob()
        await Api("signer/uploadSignature")(signatureBlob, signerAccessCode, "signature", reuseSignature)
      } else if (usingExisting && reusePreferenceChanged()) {
        await updateReusePreference()
      }

      await completeDocumentSign()
    } catch (err) {
      if (await isMissingStoredSignatureError(err)) {
        handleMissingStoredSignature()
      } else {
        setError((prev) => [...prev, "Erro ao assinar documento"])
      }
      setLoading(false)
    }
  }

  return (
    <>
      <SignNavbar
        className="signature-page-navbar"
        setModalCancel={setModalCancel}
        items={navbarItems()}
        buttonNextMobile={{
          onClick: handleUpload,
          loading,
          children: (
            <>
              <Icon id="icon-initial" className="me-1" size="16" />
              ASSINAR
            </>
          ),
        }}
      />

      <div className="signature-page">
        <div className="signature-page-content">
          <div className="signature-page-container">
            <div className="signature-card">
            <div className="dashboard-subheader">
              <h1 className="dashboard-title">Sua assinatura</h1>
            </div>

              {error.length > 0 && (
                <Alert show={true} variant="warning" onClose={() => setError([])}>
                  {error.map((err, i) => (
                    <div key={i}>{typeof err === "string" ? err : err.message}</div>
                  ))}
                </Alert>
              )}

              <Tabs activeKey={tabKey} onSelect={handleTabSelect}>
                <Tabs.Tab eventKey="custom" title="Desenhar">
                  <div className="signature-pad-wrapper">
                    Desenhe abaixo sua assinatura
                    <div className="signature-pad">
                      <canvas id="name-custom-pad" className="fluid" height="142" />
                      <Button variant="link" className="signature-pad-clear" onClick={handleClearCanvas}>
                        <Icon id="icon-trash" className="me-1" size="16" />
                        LIMPAR
                      </Button>
                    </div>
                  </div>
                </Tabs.Tab>
                <Tabs.Tab eventKey="style" title="Digitar">
                  <Row>
                    <Col xs="8">
                      <Form.Group>
                        <Input
                          name="signer_name"
                          autoComplete="off"
                          autoFocus={true}
                          label="Nome completo"
                          size="lg"
                          defaultValue={signerName}
                          onChange={(e) => setSignerName(e.target.value)}
                          maxLength="50"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  Selecione o estilo abaixo para vizualizar:
                  <div className="row d-none">
                    <div className="col-12">
                      <div className="signature-pad read-only">
                        <canvas id="name-style-pad" className="fluid" height="60" />
                      </div>
                    </div>
                  </div>
                  {signerName && signerName.length > 0 && (
                    <div className="signature-list">
                      {fonts.map((fname) => {
                        return (
                          <label
                            key={`signature-${fname}`}
                            className="signature-item"
                            onClick={() => handleChoosenSignature(`${fname}`)}
                          >
                            <Input.Radio name="signature" value="1" />
                            <div className="signature-item-body">
                              <Row style={{ fontFamily: fname }}>
                                <Col lg="12">{signerName}</Col>
                              </Row>
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </Tabs.Tab>
                <Tabs.Tab eventKey="upload" title="Enviar imagem">
                  <div className="signature-pad-wrapper">
                    Clique abaixo para anexar imagem ou bater uma foto com o celular
                    <div
                      className={`signature-pad signature-pad-upload${imagePreviewUrl ? " has-image" : ""}`}
                      onClick={handleUploadAreaClick}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault()
                          handleUploadAreaClick()
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {imagePreviewUrl && (
                        <>
                          <img
                            src={imagePreviewUrl}
                            alt="Pré-visualização da assinatura"
                            className="signature-upload-preview"
                          />
                          <Button
                            variant="link"
                            className="signature-pad-clear"
                            onClick={(event) => {
                              event.stopPropagation()
                              handleClearImage()
                            }}
                          >
                            <Icon id="icon-trash" className="me-1" size="16" />
                            LIMPAR
                          </Button>
                        </>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="signature-upload-input"
                        onChange={handleImageSelect}
                      />
                    </div>
                  </div>
                </Tabs.Tab>
              </Tabs>

              <div className="signature-reuse-option">
                <Input.Switch
                  id="reuse-signature"
                  checked={reuseSignature}
                  onChange={(event) => {
                    reuseTouchedRef.current = true
                    setReuseSignature(event.target.checked)
                  }}
                />
                <label htmlFor="reuse-signature">Usar esta assinatura em documentos futuros</label>
              </div>

              <div className="signature-action">
                <Button variant="primary" onClick={handleUpload} loading={loading} className="signature-submit-btn">
                  <Icon id="icon-initial" className="me-1" size="16" />
                  ASSINAR DOCUMENTO
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalDeny
        show={!!modalCancel}
        description={modalCancel?.description}
        params={{
          signerAccessCode,
          documentId: signDocument?.id,
          assignmentId: signDocument?.assignment?.id,
        }}
        onClose={() => setModalCancel()}
      />

      {rawImageUrl && cropModalOpen && (
        <ModalSignatureCrop
          show={cropModalOpen}
          imageUrl={rawImageUrl}
          onClose={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
    </>
  )
}

export default SignaturePage
